import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useTheme } from '../lib/theme';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Eye, EyeOff, AlertCircle } from 'lucide-react';

export default function Login() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const { theme } = useTheme();
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const user = await login(email, password);
            navigate(user.role === 'admin' ? '/admin' : '/dashboard');
        } catch (err) {
            const errorDetail = err.response?.data?.detail || '';
            if (errorDetail === 'Invalid credentials' || err.response?.status === 401) {
                setError('Email o password errati. Verifica le credenziali e riprova.');
            } else if (errorDetail === 'Account disabled') {
                setError('Account disabilitato. Contatta l\'amministratore.');
            } else if (err.response?.status === 429) {
                setError('Troppi tentativi di accesso. Attendi un minuto e riprova.');
            } else {
                setError('Errore durante l\'accesso. Riprova più tardi.');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4 relative overflow-hidden">
            {/* Noise overlay */}
            <div className="noise-overlay" />
            
            {/* Background grid */}
            <div className="absolute inset-0 opacity-10">
                <div className="absolute inset-0" style={{
                    backgroundImage: `linear-gradient(#00FF41 1px, transparent 1px), linear-gradient(90deg, #00FF41 1px, transparent 1px)`,
                    backgroundSize: '50px 50px'
                }} />
            </div>

            {/* Login card */}
            <div className="w-full max-w-md relative z-10">
                <div className="bg-[#0A0A0A] border border-white/10 p-8 shadow-2xl">
                    {/* Logo Smart Info */}
                    <div className="text-center mb-8">
                        <img 
                            src={theme === 'light' ? '/smart-info-logo-dark.svg' : '/smart-info-logo.svg'}
                            data-testid="login-logo"
                            alt="Smart Info Logo" 
                            className="h-28 w-auto mx-auto mb-4"
                        />
                        <p className="text-white/60 text-sm mt-2 font-mono">
                            SISTEMA REPORT INVESTIGATIVI
                        </p>
                    </div>

                    {/* Error message */}
                    {error && (
                        <div className="bg-red-500/10 border border-red-500 p-3 mb-6 flex items-center gap-2">
                            <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                            <span className="text-red-500 text-sm">{error}</span>
                        </div>
                    )}

                    {/* Login form */}
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="space-y-2">
                            <Label htmlFor="email" className="text-white/80 uppercase text-xs tracking-wider">
                                Email
                            </Label>
                            <Input
                                id="email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="email@esempio.it"
                                required
                                data-testid="login-email-input"
                                className="bg-black border-white/20 text-white placeholder-white/40 focus:border-[#00FF41] focus:ring-[#00FF41] rounded-none font-mono"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="password" className="text-white/80 uppercase text-xs tracking-wider">
                                Password
                            </Label>
                            <div className="relative">
                                <Input
                                    id="password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="••••••••"
                                    required
                                    data-testid="login-password-input"
                                    className="bg-black border-white/20 text-white placeholder-white/40 focus:border-[#00FF41] focus:ring-[#00FF41] rounded-none font-mono pr-10"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60"
                                >
                                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>

                        <Button
                            type="submit"
                            disabled={loading}
                            data-testid="login-submit-btn"
                            className="w-full bg-[#00FF41] text-black font-semibold uppercase tracking-widest hover:bg-[#00CC33] hover:shadow-neon transition-all duration-300 rounded-none h-12"
                        >
                            {loading ? (
                                <span className="flex items-center gap-2">
                                    <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                                    ACCESSO IN CORSO...
                                </span>
                            ) : (
                                'ACCEDI'
                            )}
                        </Button>
                    </form>

                    {/* Footer */}
                    <div className="mt-8 pt-6 border-t border-white/10 text-center">
                        <p className="text-white/40 text-xs font-mono">
                            SISTEMA RISERVATO - ACCESSO AUTORIZZATO
                        </p>
                    </div>
                </div>

                {/* Decorative corner elements */}
                <div className="absolute -top-2 -left-2 w-4 h-4 border-t-2 border-l-2 border-[#00FF41]" />
                <div className="absolute -top-2 -right-2 w-4 h-4 border-t-2 border-r-2 border-[#00FF41]" />
                <div className="absolute -bottom-2 -left-2 w-4 h-4 border-b-2 border-l-2 border-[#00FF41]" />
                <div className="absolute -bottom-2 -right-2 w-4 h-4 border-b-2 border-r-2 border-[#00FF41]" />
            </div>
        </div>
    );
}
